import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import api from '../lib/api';
import { translateDomain, allDomainsTranslated, toFrenchDomain } from '../translations';

export default function SponsorDashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [activeTab, setActiveTab] = useState('search');
  const [auditors, setAuditors] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [duration, setDuration] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedAuditor, setSelectedAuditor] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingNotes, setBookingNotes] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(null);

  const allDomains = allDomainsTranslated(lang);

  const auditorColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  const statusLabels = {
    pending: { fr: 'En attente', en: 'Pending', color: '#f59e0b' },
    accepted: { fr: 'Accepté', en: 'Accepted', color: '#10b981' },
    rejected: { fr: 'Refusé', en: 'Rejected', color: '#ef4444' },
    info_needed: { fr: 'Infos demandées', en: 'Info needed', color: '#3b82f6' },
    planning: { fr: 'Planification', en: 'Planning', color: '#8b5cf6' },
    conduct: { fr: 'En cours', en: 'In progress', color: '#06b6d4' },
    report: { fr: 'Rapport soumis', en: 'Report submitted', color: '#14b8a6' },
    closed: { fr: 'Clôturé', en: 'Closed', color: '#6b7280' }
  };

  useEffect(() => {
    if (activeTab === 'reservations') {
      loadBookings();
    }
  }, [activeTab]);

  const loadBookings = async () => {
    try {
      const response = await api.get('/bookings');
      setBookings(response.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const handleUploadAuditPlan = async (bookingId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    setLoadingMessage(lang === 'fr' ? 'Envoi du plan d\'audit...' : 'Uploading audit plan...');
    try {
      await api.post(`/bookings/${bookingId}/upload-audit-plan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(lang === 'fr' ? 'Plan envoyé !' : 'Plan sent!');
      loadBookings();
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur d\'envoi' : 'Send error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const handleCloseAudit = async (bookingId) => {
    if (!window.confirm(lang === 'fr' ? 'Clôturer cet audit ?' : 'Close this audit?')) return;

    setLoadingMessage(lang === 'fr' ? 'Clôture en cours...' : 'Closing audit...');
    try {
      await api.post(`/bookings/${bookingId}/close-audit`);
      toast.success(lang === 'fr' ? 'Audit clôturé !' : 'Audit closed!');
      loadBookings();
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const toggleDomain = (domain) => {
    const frenchDomain = toFrenchDomain(domain);
    setSelectedDomains(prev =>
      prev.includes(frenchDomain) ? prev.filter(d => d !== frenchDomain) : [...prev, frenchDomain]
    );
  };

  const removeDomain = (domain) => {
    setSelectedDomains(prev => prev.filter(d => d !== domain));
  };

  const handleSearch = async () => {
    const frenchDomains = selectedDomains.map(d => toFrenchDomain(d));
    if (selectedDomains.length === 0) {
      toast.error(lang === 'fr' ? 'Sélectionnez au moins un domaine' : 'Select at least one domain');
      return;
    }

    setLoadingMessage(lang === 'fr' ? 'Recherche en cours...' : 'Searching...');
    try {
      const params = new URLSearchParams();
      frenchDomains.forEach(d => params.append('domains', d));
      if (duration) params.append('duration', duration);

      const response = await api.get(`/auditors?${params}`);
      const filteredAuditors = response.data.filter(auditor =>
        auditor.expertise_domains.some(domain => frenchDomains.includes(toFrenchDomain(domain)))
      );
      setAuditors(filteredAuditors);

      if (filteredAuditors.length === 0) {
        toast.info(lang === 'fr' ? 'Aucun auditeur trouvé' : 'No auditors found');
      } else {
        toast.success(`${filteredAuditors.length} ${lang === 'fr' ? 'auditeur(s) trouvé(s)' : 'auditor(s) found'}`);
      }
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur de recherche' : 'Search error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({ day: prevMonthLastDay - i, isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false });
    }
    return days;
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getAuditorsForDate = (dateKey) => {
    if (!selectedAuditor) {
      return auditors.filter(auditor => auditor.availability && auditor.availability.includes(dateKey));
    }
    return selectedAuditor.availability && selectedAuditor.availability.includes(dateKey) ? [selectedAuditor] : [];
  };

  const toggleDateSelection = (year, month, day) => {
    if (!selectedAuditor) {
      toast.error(lang === 'fr' ? 'Sélectionnez d\'abord un auditeur' : 'Select an auditor first');
      return;
    }

    const dateKey = formatDateKey(year, month, day);
    if (!selectedAuditor.availability || !selectedAuditor.availability.includes(dateKey)) {
      toast.error(lang === 'fr' ? 'Cet auditeur n\'est pas disponible ce jour' : 'Auditor not available on this day');
      return;
    }

    setSelectedDates(prev =>
      prev.includes(dateKey) ? prev.filter(d => d !== dateKey) : [...prev, dateKey]
    );
  };

  const handleRequestBooking = () => {
    if (!selectedAuditor) {
      toast.error(lang === 'fr' ? 'Sélectionnez un auditeur' : 'Select an auditor');
      return;
    }

    if (selectedDates.length === 0) {
      toast.error(lang === 'fr' ? 'Sélectionnez des dates' : 'Select dates');
      return;
    }

    setShowBookingModal(true);
  };

  const confirmBooking = async () => {
    const frenchDomains = selectedDomains.map(d => toFrenchDomain(d));
    
    setShowBookingModal(false);
    setLoadingMessage(lang === 'fr' ? 'Envoi de la demande...' : 'Sending request...');
    
    try {
      const sortedDates = [...selectedDates].sort();
      await api.post('/bookings', {
        auditor_id: selectedAuditor.id,
        start_date: sortedDates[0],
        end_date: sortedDates[sortedDates.length - 1],
        domain: frenchDomains[0],
        notes: bookingNotes
      });

      toast.success(lang === 'fr' ? 'Demande envoyée !' : 'Request sent!');
      setBookingNotes('');
      setSelectedDates([]);
      setSelectedAuditor(null);
    } catch (error) {
      toast.error(lang === 'fr' ? 'Erreur d\'envoi' : 'Send error');
    } finally {
      setLoadingMessage(null);
    }
  };

  const renderCalendar = () => {
    const days = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' });

    return (
      <>
        <div style={styles.monthHeader}>
          <button style={styles.navButton} onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>‹</button>
          <div style={styles.monthTitle}>{monthName}</div>
          <button style={styles.navButton} onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>›</button>
        </div>

        <div style={styles.weekDays}>
          {['lu', 'ma', 'me', 'je', 've', 'sa', 'di'].map(day => (
            <div key={day} style={styles.weekDay}>{day}</div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {days.map((dayObj, idx) => {
            const dateKey = formatDateKey(currentMonth.getFullYear(), currentMonth.getMonth(), dayObj.day);
            const auditorsAvailable = getAuditorsForDate(dateKey);
            const isSelected = selectedDates.includes(dateKey);

            let dayStyle = { ...styles.day };

            if (!dayObj.isCurrentMonth) {
              dayStyle = { ...dayStyle, ...styles.dayOtherMonth };
            } else if (isSelected) {
              dayStyle = { ...dayStyle, ...styles.daySelected };
            } else if (auditorsAvailable.length > 0) {
              dayStyle = { ...dayStyle, ...styles.dayAvailable };
            }

            return (
              <div
                key={idx}
                style={dayStyle}
                onClick={() => dayObj.isCurrentMonth && toggleDateSelection(currentMonth.getFullYear(), currentMonth.getMonth(), dayObj.day)}
              >
                <div>{dayObj.day}</div>
                {dayObj.isCurrentMonth && auditorsAvailable.length > 0 && (
                  <div style={styles.auditorDots}>
                    {auditorsAvailable.slice(0, 3).map((auditor, auditorIdx) => {
                      const auditorIndex = auditors.findIndex(a => a.id === auditor.id);
                      const color = auditorColors[auditorIndex % auditorColors.length];
                      return <div key={auditor.id} style={{ ...styles.dot, backgroundColor: color }}></div>;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f9fafb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    content: { maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' },
    title: { fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '32px' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '32px', borderBottom: '2px solid #e5e7eb' },
    tab: { padding: '12px 24px', fontSize: '15px', fontWeight: '500', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-2px', color: '#6b7280', transition: 'all 0.2s' },
    tabActive: { color: '#111827', borderBottomColor: '#111827' },
    card: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', border: '1px solid #e5e7eb', marginBottom: '24px' },
    cardTitle: { fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px' },
    searchGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '16px', marginBottom: '24px' },
    label: { display: 'block', fontSize: '14px', fontWeight: '500', color: '#111827', marginBottom: '8px' },
    dropdown: { position: 'relative' },
    dropdownButton: { width: '100%', padding: '12px 16px', fontSize: '15px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#ffffff', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    dropdownMenu: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '300px', overflowY: 'auto' },
    dropdownItem: { padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', transition: 'background 0.2s' },
    checkbox: { width: '20px', height: '20px', borderRadius: '4px', border: '2px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    checkboxChecked: { backgroundColor: '#111827', borderColor: '#111827', color: '#ffffff' },
    tagsContainer: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' },
    tag: { padding: '6px 12px', backgroundColor: '#f3f4f6', borderRadius: '6px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' },
    tagRemove: { cursor: 'pointer', fontWeight: 'bold', color: '#6b7280' },
    input: { width: '100%', padding: '12px 16px', fontSize: '15px', border: '1px solid #e5e7eb', borderRadius: '8px' },
    searchButton: { padding: '12px 32px', fontSize: '15px', fontWeight: '500', color: '#ffffff', backgroundColor: '#111827', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' },
    auditorsList: { display: 'grid', gap: '16px' },
    auditorCard: { padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' },
    auditorCardSelected: { borderColor: '#111827', backgroundColor: '#f9fafb' },
    auditorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    auditorName: { fontSize: '18px', fontWeight: '600' },
    auditorDot: { width: '16px', height: '16px', borderRadius: '50%' },
    auditorDomains: { fontSize: '14px', color: '#6b7280', marginBottom: '8px' },
    legend: { display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' },
    legendItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#6b7280', cursor: 'pointer', padding: '8px 12px', borderRadius: '6px', transition: 'background 0.2s' },
    legendItemSelected: { backgroundColor: '#f3f4f6' },
    calendarContainer: { border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px', marginBottom: '24px' },
    monthHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    monthTitle: { fontSize: '18px', fontWeight: '600', color: '#111827', textTransform: 'capitalize' },
    navButton: { width: '32px', height: '32px', borderRadius: '6px', border: '1px solid #e5e7eb', backgroundColor: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' },
    weekDays: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' },
    weekDay: { textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', padding: '8px 0' },
    daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' },
    day: { aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '14px', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s', fontWeight: '500', position: 'relative', padding: '4px' },
    dayOtherMonth: { color: '#d1d5db' },
    dayAvailable: { backgroundColor: '#d1fae5', color: '#065f46' },
    daySelected: { backgroundColor: '#111827', color: '#ffffff' },
    auditorDots: { display: 'flex', gap: '2px', marginTop: '2px' },
    dot: { width: '6px', height: '6px', borderRadius: '50%' },
    requestButton: { width: '100%', padding: '14px', fontSize: '16px', fontWeight: '500', color: '#ffffff', backgroundColor: '#111827', border: 'none', borderRadius: '8px', cursor: 'pointer' },
    requestButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    bookingCard: { padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '16px' },
    bookingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    bookingTitle: { fontSize: '18px', fontWeight: '600' },
    statusBadge: { padding: '4px 12px', borderRadius: '6px', fontSize: '14px', fontWeight: '500' },
    bookingDetails: { fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '16px' },
    bookingActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    button: { padding: '10px 20px', fontSize: '14px', fontWeight: '500', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
    buttonPrimary: { backgroundColor: '#111827', color: '#ffffff' },
    buttonSecondary: { backgroundColor: '#ffffff', color: '#6b7280', border: '2px solid #e5e7eb' },
    uploadButton: { backgroundColor: '#ffffff', color: '#374151', border: '2px solid #e5e7eb', cursor: 'pointer' },
    fileInput: { display: 'none' },
    link: { color: '#2563eb', textDecoration: 'none', fontSize: '14px' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#ffffff', borderRadius: '12px', padding: '32px', maxWidth: '500px', width: '90%' },
    modalTitle: { fontSize: '24px', fontWeight: '600', marginBottom: '16px' },
    textarea: { width: '100%', minHeight: '100px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '15px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
    modalButtons: { display: 'flex', gap: '12px', marginTop: '24px' },
    loadingOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 99999 },
    spinner: { width: '60px', height: '60px', border: '6px solid rgba(255, 255, 255, 0.3)', borderTop: '6px solid #ffffff', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    loadingText: { marginTop: '24px', color: '#ffffff', fontSize: '18px', fontWeight: '500' }
  };

  return (
    <div style={styles.container}>
      <Navbar />

      {/* LOADING OVERLAY - BLOQUE TOUTE LA PAGE */}
      {loadingMessage && (
        <div style={styles.loadingOverlay}>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>{loadingMessage}</div>
        </div>
      )}

      <div style={styles.content}>
        <h1 style={styles.title}>
          {lang === 'fr' ? 'Tableau de bord Sponsor' : 'Sponsor Dashboard'}
        </h1>

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(activeTab === 'search' ? styles.tabActive : {}) }} onClick={() => setActiveTab('search')}>
            🔍 {lang === 'fr' ? 'Recherche' : 'Search'}
          </button>
          <button style={{ ...styles.tab, ...(activeTab === 'reservations' ? styles.tabActive : {}) }} onClick={() => setActiveTab('reservations')}>
            {lang === 'fr' ? 'Réservations' : 'Reservations'}
          </button>
        </div>

        {activeTab === 'search' && (
          <>
            <div style={styles.card}>
              <div style={styles.cardTitle}>{lang === 'fr' ? 'Rechercher des auditeurs' : 'Search auditors'}</div>

              <div style={styles.searchGrid}>
                <div>
                  <label style={styles.label}>{lang === 'fr' ? 'Domaines d\'expertise' : 'Expertise domains'}</label>
                  <div style={styles.dropdown}>
                    <button style={styles.dropdownButton} onClick={() => setDropdownOpen(!dropdownOpen)}>
                      <span style={{ color: selectedDomains.length === 0 ? '#9ca3af' : '#111827' }}>
                        {selectedDomains.length === 0 ? (lang === 'fr' ? 'Choisir des domaines...' : 'Choose domains...') : `${selectedDomains.length} domaine(s) selectionne(s)`}
                      </span>
                      <span>{dropdownOpen ? '▲' : '▼'}</span>
                    </button>

                    {dropdownOpen && (
                      <div style={styles.dropdownMenu}>
                        {allDomains.map(domain => (
                          <div key={domain} style={styles.dropdownItem} onClick={() => toggleDomain(domain)}>
                            <div style={{ ...styles.checkbox, ...(selectedDomains.includes(toFrenchDomain(domain)) ? styles.checkboxChecked : {}) }}>
                              {selectedDomains.includes(toFrenchDomain(domain)) && '✓'}
                            </div>
                            <span>{domain}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedDomains.length > 0 && (
                    <div style={styles.tagsContainer}>
                      {selectedDomains.map(domain => (
                        <div key={domain} style={styles.tag}>
                          <span>{translateDomain(domain, lang)}</span>
                          <span style={styles.tagRemove} onClick={() => removeDomain(domain)}>×</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label style={styles.label}>{lang === 'fr' ? 'Durée (jours)' : 'Duration (days)'}</label>
                  <input type="number" style={styles.input} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Ex: 3" />
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button style={styles.searchButton} onClick={handleSearch}>
                    🔍 {lang === 'fr' ? 'Rechercher' : 'Search'}
                  </button>
                </div>
              </div>
            </div>

            {auditors.length > 0 && (
              <>
                <div style={styles.card}>
                  <div style={styles.cardTitle}>{lang === 'fr' ? 'Auditeurs disponibles' : 'Available auditors'}</div>

                  <div style={styles.legend}>
                    {auditors.map((auditor, idx) => (
                      <div
                        key={auditor.id}
                        style={{
                          ...styles.legendItem,
                          ...(selectedAuditor?.id === auditor.id ? styles.legendItemSelected : {})
                        }}
                        onClick={() => {
                          setSelectedAuditor(selectedAuditor?.id === auditor.id ? null : auditor);
                          setSelectedDates([]);
                        }}
                      >
                        <div style={{ ...styles.auditorDot, backgroundColor: auditorColors[idx % auditorColors.length] }}></div>
                        <span>{auditor.name}</span>
                      </div>
                    ))}
                  </div>

                  <div style={styles.auditorsList}>
                    {auditors.map((auditor, idx) => {
                      const isSelected = selectedAuditor?.id === auditor.id;
                      return (
                        <div
                          key={auditor.id}
                          style={{
                            ...styles.auditorCard,
                            ...(isSelected ? styles.auditorCardSelected : {})
                          }}
                          onClick={() => {
                            setSelectedAuditor(isSelected ? null : auditor);
                            setSelectedDates([]);
                          }}
                        >
                          <div style={styles.auditorHeader}>
                            <div style={styles.auditorName}>{auditor.name}</div>
                            <div style={{ ...styles.auditorDot, backgroundColor: auditorColors[idx % auditorColors.length] }}></div>
                          </div>
                          <div style={styles.auditorDomains}>
                            {auditor.expertise_domains.slice(0, 3).map(d => translateDomain(d, lang)).join(', ')}
                            {auditor.expertise_domains.length > 3 && ` +${auditor.expertise_domains.length - 3}`}
                          </div>
                          {auditor.cv_url && (
                            <a href={auditor.cv_url} target="_blank" rel="noopener noreferrer" style={styles.link} onClick={(e) => e.stopPropagation()}>
                              📄 {lang === 'fr' ? 'Voir CV' : 'View CV'}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={styles.card}>
                  <div style={styles.cardTitle}>📅 {lang === 'fr' ? 'Calendrier de disponibilité' : 'Availability calendar'}</div>

                  <div style={styles.calendarContainer}>
                    {renderCalendar()}
                  </div>

                  <button 
                    style={{
                      ...styles.requestButton,
                      ...((selectedDates.length === 0 || !selectedAuditor) ? styles.requestButtonDisabled : {})
                    }} 
                    onClick={handleRequestBooking}
                    disabled={selectedDates.length === 0 || !selectedAuditor}
                  >
                    {lang === 'fr' ? 'Envoyer la demande de réservation' : 'Send booking request'} ({selectedDates.length} {lang === 'fr' ? 'jour(s)' : 'day(s)'})
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'reservations' && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>{lang === 'fr' ? 'Mes réservations' : 'My bookings'}</div>
            {bookings.length === 0 ? (
              <p style={{ color: '#6b7280' }}>{lang === 'fr' ? 'Aucune réservation' : 'No bookings'}</p>
            ) : (
              bookings.map(booking => {
                const status = statusLabels[booking.status] || statusLabels.pending;
                
                return (
                  <div key={booking.id} style={styles.bookingCard}>
                    <div style={styles.bookingHeader}>
                      <div style={styles.bookingTitle}>{booking.auditor_name}</div>
                      <div style={{ ...styles.statusBadge, backgroundColor: status.color + '20', color: status.color }}>
                        {lang === 'fr' ? status.fr : status.en}
                      </div>
                    </div>
                    <div style={styles.bookingDetails}>
                      <div>📅 {booking.start_date} → {booking.end_date}</div>
                      <div>🏷️ {translateDomain(booking.domain, lang)}</div>
                      {booking.notes && <div>📝 {booking.notes}</div>}
                      {booking.audit_plan_url && (
                        <div>
                          📄 <a href={booking.audit_plan_url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                            {lang === 'fr' ? 'Plan d\'audit' : 'Audit plan'}
                          </a>
                        </div>
                      )}
                      {booking.draft_report_url && (
                        <div>
                          📊 <a href={booking.draft_report_url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                            {lang === 'fr' ? 'Rapport d\'audit' : 'Audit report'}
                          </a>
                        </div>
                      )}
                    </div>

                    <div style={styles.bookingActions}>
                      {booking.status === 'accepted' && (
                        <>
                          <input
                            type="file"
                            id={`audit-plan-${booking.id}`}
                            style={styles.fileInput}
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => e.target.files[0] && handleUploadAuditPlan(booking.id, e.target.files[0])}
                          />
                          <label htmlFor={`audit-plan-${booking.id}`} style={{ ...styles.button, ...styles.uploadButton }}>
                            📤 {lang === 'fr' ? 'Envoyer plan d\'audit' : 'Send audit plan'}
                          </label>
                        </>
                      )}

                      {booking.status === 'report' && (
                        <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={() => handleCloseAudit(booking.id)}>
                          ✓ {lang === 'fr' ? 'Clôturer l\'audit' : 'Close audit'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {showBookingModal && (
        <div style={styles.modal} onClick={() => setShowBookingModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>{lang === 'fr' ? 'Confirmer la demande' : 'Confirm request'}</div>
            <p>{selectedAuditor.name}</p>
            <p>{selectedDates.length} {lang === 'fr' ? 'jour(s)' : 'day(s)'}: {selectedDates.sort()[0]} → {selectedDates.sort()[selectedDates.length - 1]}</p>

            <label style={styles.label}>{lang === 'fr' ? 'Notes (optionnel)' : 'Notes (optional)'}</label>
            <textarea style={styles.textarea} value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)} placeholder={lang === 'fr' ? 'Informations complémentaires...' : 'Additional information...'}></textarea>

            <div style={styles.modalButtons}>
              <button style={{ ...styles.button, ...styles.buttonSecondary }} onClick={() => setShowBookingModal(false)}>
                {lang === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
              <button style={{ ...styles.button, ...styles.buttonPrimary }} onClick={confirmBooking}>
                {lang === 'fr' ? 'Envoyer' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
